/**
 * The edge payload contract.
 *
 * This type is the single source of truth shared by the Cloudflare Worker and
 * the browser probe. The Worker (worker/src/index.ts) imports this exact shape,
 * so a change on either side fails typecheck on the other — which is why the
 * Worker source lives in this repo rather than being copy-pasted into a
 * dashboard.
 */
export interface BotManagementLike {
  /** Only present when the zone has the paid Bot Management add-on. */
  ja3Hash?: string;
  ja4?: string;
  score?: number;
  verifiedBot?: boolean;
}

export interface EdgePayload {
  /** Full request.cf passthrough. Field presence varies by plan and connection. */
  cf: Record<string, unknown> | null;
  /** True only when the paid Bot Management add-on is enabled for the zone. */
  botManagement: BotManagementLike | null;
  /**
   * Lowercased request headers. NOTE: Cloudflare reconstructs the request, so
   * header ORDER is not reliably preserved. Do not present this as an ordering
   * fingerprint. For a genuinely original Accept-Encoding, read
   * cf.clientAcceptEncoding instead (also present on the payload as
   * clientAcceptEncoding).
   */
  headers: Record<string, string>;
  /** The ORIGINAL client Accept-Encoding, before Cloudflare normalization. */
  clientAcceptEncoding: string | null;
  colo: string | null;
  /**
   * The address the edge observed this request arriving from, lifted out of
   * `cf-connecting-ip`. Cross-checked against the WebRTC STUN reflexive address
   * to detect split-tunnel VPNs, where UDP and TCP egress via different routes
   * and the page therefore presents two public identities.
   */
  edgeConnectingIp: string | null;
  ts: number;
}

/** A convenience accessor for a string field on request.cf. */
export function cfString(cf: Record<string, unknown> | null | undefined, key: string): string | null {
  if (!cf) return null;
  const v = cf[key];
  return typeof v === 'string' ? v : null;
}

/** A convenience accessor for a number field on request.cf. */
export function cfNumber(cf: Record<string, unknown> | null | undefined, key: string): number | null {
  if (!cf) return null;
  const v = cf[key];
  return typeof v === 'number' ? v : null;
}
